import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import WhatshotIcon from "@material-ui/icons/Whatshot";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from "recharts";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import TabPanel from "../../components/TabPanel";
import SaleAuditList from "../../components/SaleAuditList";
import { i18n } from "../../translate/i18n";
import {
  STAGE_ORDER,
  stageLabel,
  scoreColor,
  formatBRL,
  formatPercent,
} from "../../utils/saleAudit";

// Faixas sempre renderizadas na mesma ordem, mesmo quando o backend
// omite uma faixa sem ocorrências no período.
const BAND_ORDER = ["excelente", "bom", "regular", "ruim"];

const BAND_COLOR = {
  excelente: "#0F766E",
  bom: "#4CAF8E",
  regular: "#B7791F",
  ruim: "#E5484D",
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    overflowY: "auto",
    boxSizing: "border-box",
  },
  filters: {
    padding: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  tabsBar: {
    marginBottom: theme.spacing(2),
  },
  kpiCard: {
    padding: theme.spacing(2),
    textAlign: "center",
    height: "100%",
  },
  kpiValue: {
    fontSize: 24,
    fontWeight: 700,
  },
  section: {
    padding: theme.spacing(2),
    height: "100%",
  },
  funnelRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  funnelLabel: {
    width: 110,
    flexShrink: 0,
    fontSize: 13,
  },
  funnelTrack: {
    flex: 1,
    background: theme.palette.type === "dark" ? "#333" : "#eee",
    borderRadius: 4,
    height: 20,
    overflow: "hidden",
  },
  funnelBar: {
    height: "100%",
    background: theme.palette.primary.main,
    borderRadius: 4,
    minWidth: 2,
    transition: "width .3s",
  },
  funnelCount: {
    width: 36,
    textAlign: "right",
    fontSize: 13,
    fontWeight: 600,
  },
  hotCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: 16,
    borderLeft: "4px solid #E5484D",
  },
  chartBox: {
    width: "100%",
    height: 220,
  },
  clickableRow: {
    cursor: "pointer",
  },
  scoreCell: {
    fontWeight: 700,
    fontSize: 16,
  },
}));

const formatTrendDate = (date) => {
  const parts = String(date || "").split("-");
  if (parts.length !== 3) return date || "";
  return `${parts[2]}/${parts[1]}`;
};

const SaleAuditDashboard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  // Embed (aba do ERP): mesmo padrão de altura/scroll do CrmDashboard —
  // sem altura explícita a página renderia cortada dentro do iframe.
  const embed = sessionStorage.getItem("whaticketEmbed") === "1";

  const [tab, setTab] = useState("quality");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [whatsappId, setWhatsappId] = useState("");
  const [userId, setUserId] = useState("");

  const [whatsApps, setWhatsApps] = useState([]);
  const [users, setUsers] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: whatsData } = await api.get("/whatsapp/");
        setWhatsApps(Array.isArray(whatsData) ? whatsData : []);
      } catch (err) {
        toastError(err);
      }
      try {
        const { data: usersData } = await api.get("/users/");
        setUsers(usersData?.users || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (whatsappId) params.whatsappId = whatsappId;
      if (userId) params.userId = userId;
      const { data: res } = await api.get("/sale-audits/dashboard", { params });
      setData(res);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, whatsappId, userId]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  // Atualiza quando uma nova auditoria chega (debounced).
  useEffect(() => {
    const socket = openSocket();
    const bump = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(fetchData, 1500);
    };
    socket.on("saleAudit", bump);
    return () => {
      socket.disconnect();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchData]);

  const quality = data?.quality || {};
  // Fallback para a raiz: o payload antigo (pré-v3) entregava o bloco de
  // venda sem o wrapper `sales`, e a tela não pode quebrar durante o deploy.
  const sales = data?.sales || data || {};

  const distribution = Array.isArray(quality.distribution)
    ? quality.distribution
    : [];
  // Já vem ordenado pior→melhor do backend (15 categorias fixas).
  const categoryAverages = Array.isArray(quality.categoryAverages)
    ? quality.categoryAverages
    : [];
  const agentRanking = Array.isArray(quality.agentRanking)
    ? quality.agentRanking
    : [];
  const needsAttention = Array.isArray(quality.needsAttention)
    ? quality.needsAttention
    : [];
  const trend = Array.isArray(quality.trend) ? quality.trend : [];

  const worstCategories = categoryAverages.slice(0, 5);
  const restCategories = categoryAverages.slice(5);

  const countByBand = {};
  distribution.forEach((item) => {
    countByBand[item.band] = Number(item.count) || 0;
  });
  const distributionTotal = BAND_ORDER.reduce(
    (acc, band) => acc + (countByBand[band] || 0),
    0,
  );
  const badConversations = (countByBand.regular || 0) + (countByBand.ruim || 0);

  const trendData = trend.map((point) => ({
    ...point,
    label: formatTrendDate(point.date),
    avgScore: Math.round(Number(point.avgScore) || 0),
  }));

  const qualityKpis = [
    {
      key: "audited",
      label: i18n.t("saleAuditDashboard.kpis.audited"),
      value: quality.audited ?? 0,
    },
    {
      key: "avgScore",
      label: i18n.t("saleAuditDashboard.kpis.avgScore"),
      value: Math.round(Number(quality.avgScore) || 0),
      color: scoreColor(quality.avgScore),
    },
    {
      key: "badConversations",
      label: i18n.t("saleAuditDashboard.kpis.badConversations"),
      value: badConversations,
      color: badConversations > 0 ? "#E5484D" : undefined,
    },
    {
      key: "agents",
      label: i18n.t("saleAuditDashboard.kpis.agents"),
      value: agentRanking.length,
    },
  ];

  const totals = sales.totals || {};
  const funnel = Array.isArray(sales.funnel) ? sales.funnel : [];
  const lostReasons = Array.isArray(sales.lostReasons) ? sales.lostReasons : [];
  const salesRanking = Array.isArray(sales.ranking) ? sales.ranking : [];
  const hotPending = sales.hotPending || { count: 0, value: 0 };

  const funnelByStage = {};
  funnel.forEach((f) => {
    funnelByStage[f.stage] = f.count;
  });
  const funnelMax = Math.max(1, ...funnel.map((f) => Number(f.count) || 0));

  const salesKpis = [
    {
      key: "conversion",
      label: i18n.t("saleAuditDashboard.kpis.conversion"),
      value: formatPercent(totals.conversion),
    },
    {
      key: "wonValue",
      label: i18n.t("saleAuditDashboard.kpis.wonValue"),
      value: formatBRL(totals.wonValue ?? 0),
      color: "#0F766E",
      sub: `${totals.wonCount ?? 0}x`,
    },
    {
      key: "lostValue",
      label: i18n.t("saleAuditDashboard.kpis.lostValue"),
      value: formatBRL(totals.lostValue ?? 0),
      color: "#E5484D",
      sub: `${totals.lostCount ?? 0}x`,
    },
  ];

  const renderKpis = (items) => (
    <Grid container spacing={2} style={{ marginBottom: 8 }}>
      {items.map((kpi) => (
        <Grid item xs={6} sm={4} md key={kpi.key}>
          <Paper className={classes.kpiCard} variant="outlined">
            <Typography variant="body2" color="textSecondary">
              {kpi.label}
            </Typography>
            <Typography
              className={classes.kpiValue}
              style={kpi.color ? { color: kpi.color } : undefined}
            >
              {kpi.value}
            </Typography>
            {kpi.sub && (
              <Typography variant="caption" color="textSecondary">
                {kpi.sub}
              </Typography>
            )}
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <div
      className={classes.root}
      style={{
        minHeight: embed ? "100vh" : "calc(100vh - 48px)",
        height: embed ? "100vh" : "auto",
      }}
    >
      <Typography variant="h6" gutterBottom>
        {i18n.t("saleAuditDashboard.title")}
      </Typography>

      <Paper className={classes.filters} variant="outlined">
        <TextField
          label={i18n.t("saleAuditDashboard.filters.dateFrom")}
          type="date"
          size="small"
          variant="outlined"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={i18n.t("saleAuditDashboard.filters.dateTo")}
          type="date"
          size="small"
          variant="outlined"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel shrink htmlFor="sale-audit-conn-filter">
            {i18n.t("saleAuditDashboard.filters.connection")}
          </InputLabel>
          <Select
            native
            label={i18n.t("saleAuditDashboard.filters.connection")}
            value={whatsappId}
            onChange={(e) => setWhatsappId(e.target.value)}
            inputProps={{ id: "sale-audit-conn-filter" }}
          >
            <option value="">
              {i18n.t("saleAuditDashboard.filters.allConnections")}
            </option>
            {whatsApps.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel shrink htmlFor="sale-audit-user-filter">
            {i18n.t("saleAuditDashboard.filters.user")}
          </InputLabel>
          <Select
            native
            label={i18n.t("saleAuditDashboard.filters.user")}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            inputProps={{ id: "sale-audit-user-filter" }}
          >
            <option value="">
              {i18n.t("saleAuditDashboard.filters.allUsers")}
            </option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Paper variant="outlined" className={classes.tabsBar}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            label={i18n.t("saleAuditDashboard.tabs.quality")}
            value="quality"
          />
          <Tab
            label={i18n.t("saleAuditDashboard.tabs.conversations")}
            value="conversations"
          />
          <Tab label={i18n.t("saleAuditDashboard.tabs.sales")} value="sales" />
        </Tabs>
      </Paper>

      {loading && !data && (
        <Typography variant="body2" color="textSecondary">
          {i18n.t("saleAuditDashboard.loading")}
        </Typography>
      )}

      {data && (
        <>
          <TabPanel value={tab} name="quality">
            {renderKpis(qualityKpis)}

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper className={classes.section} variant="outlined">
                  <Typography variant="subtitle1" gutterBottom>
                    {i18n.t("saleAuditDashboard.quality.distribution.title")}
                  </Typography>
                  {distributionTotal === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("saleAuditDashboard.quality.distribution.empty")}
                    </Typography>
                  ) : (
                    BAND_ORDER.map((band) => {
                      const count = countByBand[band] || 0;
                      const pct = (count / distributionTotal) * 100;
                      return (
                        <div key={band} className={classes.funnelRow}>
                          <span className={classes.funnelLabel}>
                            {i18n.t(`saleAuditDashboard.quality.bands.${band}`)}
                          </span>
                          <div className={classes.funnelTrack}>
                            <div
                              className={classes.funnelBar}
                              style={{
                                width: `${pct}%`,
                                background: BAND_COLOR[band],
                              }}
                            />
                          </div>
                          <span
                            className={classes.funnelCount}
                            style={{ color: BAND_COLOR[band] }}
                          >
                            {count}
                          </span>
                        </div>
                      );
                    })
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={8}>
                <Paper className={classes.section} variant="outlined">
                  <Typography variant="subtitle1" gutterBottom>
                    {i18n.t("saleAuditDashboard.quality.trend.title")}
                  </Typography>
                  {trendData.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("saleAuditDashboard.quality.trend.empty")}
                    </Typography>
                  ) : (
                    <div className={classes.chartBox}>
                      <ResponsiveContainer>
                        <LineChart
                          data={trendData}
                          margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="label"
                            stroke={theme.palette.text.secondary}
                          />
                          <YAxis
                            domain={[0, 100]}
                            allowDecimals={false}
                            stroke={theme.palette.text.secondary}
                          />
                          <ChartTooltip
                            formatter={(value) => [
                              value,
                              i18n.t(
                                "saleAuditDashboard.quality.trend.avgScore",
                              ),
                            ]}
                          />
                          <Line
                            type="monotone"
                            dataKey="avgScore"
                            stroke={theme.palette.primary.main}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Paper
              className={classes.section}
              variant="outlined"
              style={{ marginTop: 16 }}
            >
              <Typography variant="subtitle1" gutterBottom>
                {i18n.t("saleAuditDashboard.training.title")}
              </Typography>
              {categoryAverages.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("saleAuditDashboard.training.empty")}
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("saleAuditDashboard.training.worst")}
                    </Typography>
                    {worstCategories.map((cat) => {
                      const avg = Math.round(Number(cat.avgScore) || 0);
                      return (
                        <div key={cat.key} className={classes.funnelRow}>
                          <span className={classes.funnelLabel}>
                            {cat.label}
                          </span>
                          <div className={classes.funnelTrack}>
                            <div
                              className={classes.funnelBar}
                              style={{
                                width: `${Math.max(0, Math.min(100, avg))}%`,
                                background: scoreColor(avg),
                              }}
                            />
                          </div>
                          <span
                            className={classes.funnelCount}
                            style={{ color: scoreColor(avg) }}
                          >
                            {avg}
                          </span>
                        </div>
                      );
                    })}
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("saleAuditDashboard.training.rest")}
                    </Typography>
                    {restCategories.map((cat) => {
                      const avg = Math.round(Number(cat.avgScore) || 0);
                      return (
                        <div
                          key={cat.key}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                            padding: "2px 0",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                          }}
                        >
                          <Typography variant="body2">{cat.label}</Typography>
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 700, color: scoreColor(avg) }}
                          >
                            {avg}
                          </Typography>
                        </div>
                      );
                    })}
                  </Grid>
                </Grid>
              )}
            </Paper>

            <Grid container spacing={2} style={{ marginTop: 0 }}>
              <Grid item xs={12} md={6}>
                <Paper className={classes.section} variant="outlined">
                  <Typography variant="subtitle1" gutterBottom>
                    {i18n.t("saleAuditDashboard.quality.ranking.title")}
                  </Typography>
                  {agentRanking.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("saleAuditDashboard.quality.ranking.empty")}
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {i18n.t("saleAuditDashboard.quality.ranking.user")}
                          </TableCell>
                          <TableCell align="right">
                            {i18n.t(
                              "saleAuditDashboard.quality.ranking.audited",
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {i18n.t(
                              "saleAuditDashboard.quality.ranking.avgScore",
                            )}
                          </TableCell>
                          <TableCell>
                            {i18n.t(
                              "saleAuditDashboard.quality.ranking.worstCategory",
                            )}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {agentRanking.map((row) => {
                          const worst = row.worstCategory || null;
                          return (
                            <TableRow key={row.userId ?? row.userName}>
                              <TableCell>{row.userName || "—"}</TableCell>
                              <TableCell align="right">{row.audited}</TableCell>
                              <TableCell
                                align="right"
                                className={classes.scoreCell}
                                style={{ color: scoreColor(row.avgScore) }}
                              >
                                {Math.round(Number(row.avgScore) || 0)}
                              </TableCell>
                              <TableCell>
                                {worst ? (
                                  <>
                                    <Typography variant="body2">
                                      {worst.label}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      style={{
                                        color: scoreColor(worst.avgScore),
                                        fontWeight: 700,
                                      }}
                                    >
                                      {Math.round(Number(worst.avgScore) || 0)}
                                    </Typography>
                                  </>
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper className={classes.section} variant="outlined">
                  <Typography variant="subtitle1">
                    {i18n.t("saleAuditDashboard.quality.needsAttention.title")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    gutterBottom
                  >
                    {i18n.t(
                      "saleAuditDashboard.quality.needsAttention.description",
                    )}
                  </Typography>
                  {needsAttention.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t(
                        "saleAuditDashboard.quality.needsAttention.empty",
                      )}
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">
                            {i18n.t(
                              "saleAuditDashboard.quality.needsAttention.score",
                            )}
                          </TableCell>
                          <TableCell>
                            {i18n.t(
                              "saleAuditDashboard.quality.needsAttention.contact",
                            )}
                          </TableCell>
                          <TableCell>
                            {i18n.t(
                              "saleAuditDashboard.quality.needsAttention.user",
                            )}
                          </TableCell>
                          <TableCell>
                            {i18n.t(
                              "saleAuditDashboard.quality.needsAttention.topIssue",
                            )}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {needsAttention.map((item) => (
                          <TableRow
                            key={item.ticketId}
                            hover
                            className={classes.clickableRow}
                            /* Mesmo caminho da lista de auditorias: a tela cheia
                               da auditoria daquele atendimento. */
                            onClick={() =>
                              history.push("/sale-audits/" + item.ticketId)
                            }
                          >
                            <TableCell
                              align="center"
                              className={classes.scoreCell}
                              style={{ color: scoreColor(item.score) }}
                            >
                              {Math.round(Number(item.score) || 0)}
                            </TableCell>
                            <TableCell>{item.contactName || "—"}</TableCell>
                            <TableCell>{item.userName || "—"}</TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {item.topIssue || "—"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tab} name="conversations">
            <SaleAuditList />
          </TabPanel>

          <TabPanel value={tab} name="sales">
            {hotPending.count > 0 && (
              <Paper className={classes.hotCard} variant="outlined">
                <WhatshotIcon style={{ color: "#E5484D", fontSize: 32 }} />
                <div style={{ flex: 1 }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    {hotPending.count}{" "}
                    {i18n.t("saleAuditDashboard.hotPending.title")} ·{" "}
                    {formatBRL(hotPending.value ?? 0)}{" "}
                    {i18n.t("saleAuditDashboard.hotPending.inNegotiation")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("saleAuditDashboard.hotPending.description")}
                  </Typography>
                </div>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => history.push("/sale-audits")}
                >
                  {i18n.t("saleAuditDashboard.hotPending.cta")}
                </Button>
              </Paper>
            )}

            {renderKpis(salesKpis)}

            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Paper className={classes.section} variant="outlined">
                  <Typography variant="subtitle1" gutterBottom>
                    {i18n.t("saleAuditDashboard.funnel.title")}
                  </Typography>
                  {STAGE_ORDER.map((stage) => {
                    const count = Number(funnelByStage[stage]) || 0;
                    return (
                      <div key={stage} className={classes.funnelRow}>
                        <span className={classes.funnelLabel}>
                          {stageLabel(stage)}
                        </span>
                        <div className={classes.funnelTrack}>
                          <div
                            className={classes.funnelBar}
                            style={{ width: `${(count / funnelMax) * 100}%` }}
                          />
                        </div>
                        <span className={classes.funnelCount}>{count}</span>
                      </div>
                    );
                  })}
                </Paper>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper className={classes.section} variant="outlined">
                  <Typography variant="subtitle1" gutterBottom>
                    {i18n.t("saleAuditDashboard.lostReasons.title")}
                  </Typography>
                  {lostReasons.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("saleAuditDashboard.lostReasons.empty")}
                    </Typography>
                  ) : (
                    lostReasons.map((item) => (
                      <div
                        key={item.reason}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          padding: "4px 0",
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <Typography variant="body2">{item.reason}</Typography>
                        <Typography variant="body2" style={{ fontWeight: 700 }}>
                          {item.count}
                        </Typography>
                      </div>
                    ))
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper className={classes.section} variant="outlined">
                  <Typography variant="subtitle1" gutterBottom>
                    {i18n.t("saleAuditDashboard.ranking.title")}
                  </Typography>
                  {salesRanking.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("saleAuditDashboard.ranking.empty")}
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {i18n.t("saleAuditDashboard.ranking.user")}
                          </TableCell>
                          <TableCell align="right">
                            {i18n.t("saleAuditDashboard.ranking.audited")}
                          </TableCell>
                          <TableCell align="right">
                            {i18n.t("saleAuditDashboard.ranking.conversion")}
                          </TableCell>
                          <TableCell align="right">
                            {i18n.t("saleAuditDashboard.ranking.avgScore")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {salesRanking.map((row) => (
                          <TableRow key={row.userId}>
                            <TableCell>{row.userName}</TableCell>
                            <TableCell align="right">{row.audited}</TableCell>
                            <TableCell align="right">
                              {formatPercent(row.conversion)}
                            </TableCell>
                            <TableCell align="right">
                              {Math.round(Number(row.avgScore) || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
        </>
      )}
    </div>
  );
};

export default SaleAuditDashboard;
